import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarRange, ChevronLeft, ChevronRight, Inbox, GripVertical } from "lucide-react";
import { useTaskEditor } from "@/context/TaskEditorContext";
import {
  DndContext,
  closestCorners,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
} from "@dnd-kit/core";

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function mondayOf(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function toKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function todayKey() {
  return toKey(new Date());
}

const priorityBadge = (p) => {
  switch (p) {
    case "Hoch": return "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300";
    case "Mittel": return "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300";
    default: return "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300";
  }
};

function TaskChip({ task, onToggle, onEdit, dragProps }) {
  return (
    <div
      className="flex items-start gap-2 p-2 rounded-lg border bg-card shadow-sm"
      data-testid={`week-task-${task.id}`}
    >
      <button
        type="button"
        className="mt-0.5 text-muted-foreground/50 hover:text-foreground cursor-grab active:cursor-grabbing touch-none shrink-0"
        style={{ touchAction: "none" }}
        {...dragProps.attributes}
        {...dragProps.listeners}
        data-testid={`week-task-drag-${task.id}`}
        aria-label="Verschieben"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <Checkbox
        checked={task.status === "Erledigt"}
        onCheckedChange={() => onToggle(task)}
        className="mt-0.5"
        data-testid={`week-task-check-${task.id}`}
      />
      <button
        type="button"
        className="flex-1 min-w-0 text-left"
        onClick={onEdit ? () => onEdit(task) : undefined}
        data-testid={`week-task-edit-${task.id}`}
        title="Bearbeiten"
      >
        <p className={`text-sm font-medium break-words ${task.status === "Erledigt" ? "line-through text-muted-foreground" : ""}`}>
          {task.title}
        </p>
        <Badge variant="secondary" className={`mt-1 ${priorityBadge(task.priority)}`}>{task.priority}</Badge>
      </button>
    </div>
  );
}

function DraggableTask({ task, onToggle, onEdit }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
    data: { task },
  });
  return (
    <div ref={setNodeRef} className={isDragging ? "opacity-40" : ""}>
      <TaskChip task={task} onToggle={onToggle} onEdit={onEdit} dragProps={{ attributes, listeners }} />
    </div>
  );
}

function DroppableColumn({ id, children, className = "", isToday = false, testId }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`${className} transition-colors ${isOver ? "bg-primary/10 ring-2 ring-primary/40" : ""} ${isToday ? "ring-1 ring-primary/30" : ""}`}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export default function MeineWoche({ embedded = false }) {
  const toast = useToast();
  const { openEdit } = useTaskEditor();
  const [scope, setScope] = useState("mine");
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [board, setBoard] = useState({ pool: [], days: {} });
  const [loading, setLoading] = useState(true);
  const [activeTask, setActiveTask] = useState(null);

  const startKey = toKey(weekStart);
  const dayKeys = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return toKey(d);
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } })
  );

  const fetchBoard = useCallback(async () => {
    try {
      const res = await api.get("/week-plan", { params: { start: startKey, scope } });
      setBoard({ pool: res.data?.pool || [], days: res.data?.days || {} });
    } catch (e) {
      toast.error("Wochenplan konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [startKey, scope, toast]);

  useEffect(() => { setLoading(true); fetchBoard(); }, [fetchBoard]);

  useEffect(() => {
    const onChange = () => fetchBoard();
    window.addEventListener("tasks-changed", onChange);
    return () => window.removeEventListener("tasks-changed", onChange);
  }, [fetchBoard]);

  const findTask = (id) => {
    if (board.pool.some((t) => t.id === id)) return board.pool.find((t) => t.id === id);
    for (const k of Object.keys(board.days)) {
      const found = (board.days[k] || []).find((t) => t.id === id);
      if (found) return found;
    }
    return null;
  };

  const containerOfTask = (id) => {
    if (board.pool.some((t) => t.id === id)) return "pool";
    for (const k of Object.keys(board.days)) {
      if ((board.days[k] || []).some((t) => t.id === id)) return k;
    }
    return null;
  };

  const handleDragStart = (event) => {
    setActiveTask(findTask(event.active.id));
  };

  const handleDragEnd = async (event) => {
    setActiveTask(null);
    const { active, over } = event;
    if (!over) return;
    const taskId = active.id;
    const from = containerOfTask(taskId);
    const to = over.id;
    if (!to || from === to) return;

    const task = findTask(taskId);
    if (!task) return;

    // optimistic local update
    setBoard((prev) => {
      const next = { pool: [...prev.pool], days: { ...prev.days } };
      // remove from source
      if (from === "pool") next.pool = next.pool.filter((t) => t.id !== taskId);
      else next.days[from] = (next.days[from] || []).filter((t) => t.id !== taskId);
      // add to target
      if (to === "pool") next.pool = [task, ...next.pool];
      else next.days[to] = [...(next.days[to] || []), task];
      return next;
    });

    try {
      if (to === "pool") {
        await api.delete(`/week-plan/${taskId}`, { params: { date: from, scope } });
      } else {
        await api.post("/week-plan", { task_id: taskId, date: to, scope });
      }
    } catch (e) {
      toast.error("Verschieben fehlgeschlagen");
      fetchBoard();
    }
  };

  const toggleDone = async (task) => {
    const newStatus = task.status === "Erledigt" ? "Offen" : "Erledigt";
    setBoard((prev) => {
      const upd = (t) => (t.id === task.id ? { ...t, status: newStatus } : t);
      return { pool: prev.pool.map(upd), days: Object.fromEntries(Object.entries(prev.days).map(([k, v]) => [k, v.map(upd)])) };
    });
    try {
      await api.put(`/tasks/${task.id}`, { status: newStatus });
      window.dispatchEvent(new Event("tasks-changed"));
    } catch (e) {
      toast.error("Status konnte nicht geändert werden");
      fetchBoard();
    }
  };

  const shiftWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(mondayOf(d));
  };

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const rangeLabel = `${weekStart.toLocaleDateString("de-DE", { day: "numeric", month: "short" })} – ${weekEnd.toLocaleDateString("de-DE", { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <div className="space-y-5" data-testid="meine-woche-page">
      <div className="flex flex-col gap-3">
        {!embedded && (
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <CalendarRange className="w-7 h-7 text-primary" />
            Wochenplanung
          </h1>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Tabs value={scope} onValueChange={setScope}>
            <TabsList>
              <TabsTrigger value="mine" data-testid="week-tab-mine">Meine Woche</TabsTrigger>
              <TabsTrigger value="team" data-testid="week-tab-team">Team-Woche</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => shiftWeek(-1)} data-testid="week-prev-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="week-today-btn">
              Diese Woche
            </Button>
            <span className="text-sm font-medium min-w-40 text-center" data-testid="week-range-label">{rangeLabel}</span>
            <Button variant="outline" size="icon" onClick={() => shiftWeek(1)} data-testid="week-next-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          {/* Pool */}
          <DroppableColumn
            id="pool"
            testId="week-pool"
            className="rounded-xl border-2 border-dashed p-3 min-h-24 bg-muted/20"
          >
            <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-muted-foreground">
              <Inbox className="w-4 h-4" />
              Aufgaben-Pool ({board.pool.length})
            </div>
            {board.pool.length === 0 ? (
              <p className="text-sm text-muted-foreground">Keine offenen Aufgaben – alles verplant.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                {board.pool.map((t) => <DraggableTask key={t.id} task={t} onToggle={toggleDone} onEdit={openEdit} />)}
              </div>
            )}
          </DroppableColumn>

          {/* Week grid */}
          <div className="mt-4 overflow-x-auto pb-2">
            <div className="grid grid-cols-7 gap-3 min-w-[900px]">
              {dayKeys.map((key, i) => {
                const d = new Date(weekStart);
                d.setDate(d.getDate() + i);
                const isToday = key === todayKey();
                const tasks = board.days[key] || [];
                return (
                  <DroppableColumn
                    key={key}
                    id={key}
                    testId={`week-col-${key}`}
                    isToday={isToday}
                    className="rounded-xl border bg-card p-2 min-h-[220px] flex flex-col"
                  >
                    <div className={`text-center pb-2 mb-2 border-b ${isToday ? "text-primary font-bold" : "font-medium"}`}>
                      <div className="text-xs uppercase tracking-wide">{WEEKDAYS[i]}</div>
                      <div className="text-sm">{d.getDate()}.{d.getMonth() + 1}.</div>
                    </div>
                    <div className="space-y-2 flex-1">
                      {tasks.map((t) => <DraggableTask key={t.id} task={t} onToggle={toggleDone} onEdit={openEdit} />)}
                    </div>
                  </DroppableColumn>
                );
              })}
            </div>
          </div>

          <DragOverlay>
            {activeTask ? (
              <div className="opacity-90 rotate-1">
                <TaskChip task={activeTask} onToggle={() => {}} dragProps={{ attributes: {}, listeners: {} }} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}
